(() => {
  if (customElements.get('customer-reviews-carousel')) return;

  class CustomerReviewsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedReady) return;

      this.track = this.querySelector('[data-crc-track]');
      this.originalCards = Array.from(this.querySelectorAll('[data-crc-card]'));
      this.cards = this.originalCards;
      this.previousButton = this.querySelector('[data-crc-previous]');
      this.nextButton = this.querySelector('[data-crc-next]');
      this.navigation = this.querySelector('[data-crc-navigation]');
      this.pagination = this.querySelector('[data-crc-pagination]');
      this.abortController = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isConnectedReady = true;
      this.isPointerDragging = false;
      this.hasPointerMoved = false;
      this.scrollFrame = null;
      this.scrollSettleTimer = null;
      this.refreshFrame = null;
      this.repositionFrame = null;
      this.autoplayTimer = null;
      this.loopClones = [];
      this.cloneCount = 0;
      this.loopEnabled = false;
      this.currentLogicalIndex = 0;
      this.navigationQueue = [];
      this.isNavigationAnimating = false;
      this.isRepositioning = false;

      if (!this.track || this.originalCards.length === 0) return;

      this.createLoopClones();

      const signal = this.abortController.signal;

      this.previousButton?.addEventListener('click', () => {
        this.move(-1);
        this.restartAutoplay();
      }, { signal });

      this.nextButton?.addEventListener('click', () => {
        this.move(1);
        this.restartAutoplay();
      }, { signal });

      this.pagination?.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-crc-dot]');
        if (!dot) return;
        this.goTo(Number(dot.dataset.crcDot));
        this.restartAutoplay();
      }, { signal });

      this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true, signal });
      this.track.addEventListener('scrollend', () => this.handleScrollSettled(), { signal });
      this.track.addEventListener('pointerdown', (event) => this.startPointerDrag(event), { signal });
      this.track.addEventListener('pointermove', (event) => this.handlePointerDrag(event), { signal });
      this.track.addEventListener('pointerup', (event) => this.endPointerDrag(event), { signal });
      this.track.addEventListener('pointercancel', (event) => this.endPointerDrag(event), { signal });

      if (this.dataset.pauseOnHover === 'true') {
        this.addEventListener('mouseenter', () => this.stopAutoplay(), { signal });
        this.addEventListener('mouseleave', () => this.startAutoplay(), { signal });
        this.addEventListener('focusin', () => this.stopAutoplay(), { signal });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.startAutoplay();
        }, { signal });
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      }, { signal });

      document.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest?.('[data-crc-card]');
        if (!card || !this.contains(card)) return;
        this.stopAutoplay();
        this.goTo(this.originalCards.indexOf(card), 'auto', true);
      }, { signal });

      this.resizeObserver = new ResizeObserver(() => this.scheduleRefresh());
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.track);

      this.scheduleRefresh();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.resizeObserver?.disconnect();
      this.abortController?.abort();
      this.removeLoopClones();

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.scrollSettleTimer) window.clearTimeout(this.scrollSettleTimer);
      if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
      if (this.repositionFrame) cancelAnimationFrame(this.repositionFrame);

      this.isConnectedReady = false;
    }

    createLoopClones() {
      if (this.originalCards.length <= 1 || this.loopClones.length > 0) return;

      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      this.originalCards.forEach((card, index) => {
        beforeFragment.append(this.createClone(card, index, 'before'));
        afterFragment.append(this.createClone(card, index, 'after'));
      });

      this.track.insertBefore(beforeFragment, this.originalCards[0]);
      this.track.append(afterFragment);
      this.loopClones = Array.from(this.track.querySelectorAll('[data-crc-clone]'));
      this.cloneCount = this.originalCards.length;
      this.loopClones.forEach((clone) => {
        clone.hidden = true;
      });
    }

    createClone(card, index, position) {
      const clone = card.cloneNode(true);
      clone.removeAttribute('data-crc-card');
      clone.setAttribute('data-crc-clone', `${position}-${index}`);
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.hidden = true;

      [clone, ...clone.querySelectorAll('*')].forEach((element) => {
        element.removeAttribute('id');
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify')) {
            element.removeAttribute(attribute.name);
          }
        });
      });

      return clone;
    }

    removeLoopClones() {
      this.loopClones.forEach((clone) => clone.remove());
      this.loopClones = [];
      this.cloneCount = 0;
      this.loopEnabled = false;
    }

    getGap() {
      const styles = window.getComputedStyle(this.track);
      return Number.parseFloat(styles.columnGap || styles.gap) || 0;
    }

    getStep() {
      if (!this.originalCards[0]) return 0;
      return this.originalCards[0].getBoundingClientRect().width + this.getGap();
    }

    getVisibleCapacity() {
      const step = this.getStep();
      if (step <= 0) return 1;
      return Math.max(1, Math.floor((this.track.clientWidth + this.getGap() + 1) / step));
    }

    getVirtualIndex() {
      const step = this.getStep();
      if (step <= 0) return 0;
      return Math.max(0, Math.round(this.track.scrollLeft / step));
    }

    getCurrentPage() {
      const count = this.originalCards.length;
      if (count <= 1) return 0;

      const offset = this.loopEnabled ? this.cloneCount : 0;
      const virtualIndex = this.getVirtualIndex();
      return ((virtualIndex - offset) % count + count) % count;
    }

    getPageCount() {
      return Math.max(1, this.originalCards.length);
    }

    scheduleRefresh() {
      if (this.refreshFrame) return;

      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshFrame = null;
        this.refresh();
      });
    }

    refresh() {
      if (!this.track) return;

      const logicalIndex = Math.min(this.getPageCount() - 1, Math.max(0, this.currentLogicalIndex || 0));
      const hasOverflow = this.originalCards.length > this.getVisibleCapacity();
      const shouldLoop = hasOverflow && this.cloneCount > 0;

      this.navigationQueue = [];
      this.isNavigationAnimating = false;

      this.loopClones.forEach((clone) => {
        clone.hidden = !shouldLoop;
      });

      this.loopEnabled = shouldLoop;
      this.dataset.hasOverflow = String(hasOverflow);

      this.buildPagination();

      const targetIndex = shouldLoop ? this.cloneCount + logicalIndex : logicalIndex;
      this.scrollToVirtualIndex(targetIndex, 'auto');
      this.currentLogicalIndex = logicalIndex;
      this.updatePagination();

      if (this.navigation) this.navigation.hidden = !hasOverflow;
      if (this.pagination) this.pagination.hidden = !hasOverflow;

      if (hasOverflow) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    }

    buildPagination() {
      if (!this.pagination) return;

      const pageCount = this.getPageCount();
      if (this.pagination.children.length === pageCount) return;

      this.pagination.replaceChildren();

      for (let index = 0; index < pageCount; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'customer-reviews-carousel__dot';
        dot.dataset.crcDot = String(index);
        dot.setAttribute('aria-label', `${this.dataset.paginationLabel || 'Review'} ${index + 1}`);
        this.pagination.append(dot);
      }
    }

    updatePagination() {
      const currentPage = this.getCurrentPage();
      this.currentLogicalIndex = currentPage;

      if (!this.pagination) return;

      Array.from(this.pagination.children).forEach((dot, index) => {
        if (index === currentPage) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    queueUpdate() {
      if (!this.scrollFrame) {
        this.scrollFrame = requestAnimationFrame(() => {
          this.updatePagination();
          this.scrollFrame = null;
        });
      }

      if (this.scrollSettleTimer) window.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = window.setTimeout(() => {
        this.scrollSettleTimer = null;
        this.handleScrollSettled();
      }, 180);
    }

    handleScrollSettled() {
      if (this.isPointerDragging || this.isRepositioning) return;

      if (this.scrollSettleTimer) {
        window.clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = null;
      }

      const wasRepositioned = this.normalizeLoopPosition();
      this.isNavigationAnimating = false;

      if (this.navigationQueue.length === 0) return;

      const direction = this.navigationQueue.shift();
      const continueNavigation = () => this.performMove(direction);

      if (wasRepositioned) {
        requestAnimationFrame(continueNavigation);
      } else {
        continueNavigation();
      }
    }

    scrollToVirtualIndex(index, requestedBehavior = 'smooth') {
      const behavior = this.reducedMotion.matches ? 'auto' : requestedBehavior;
      const left = Math.max(0, index * this.getStep());

      if (behavior === 'auto') {
        this.jumpToPosition(left);
        return 'auto';
      }

      this.track.scrollTo({
        left,
        behavior
      });

      return behavior;
    }

    jumpToPosition(left) {
      if (this.repositionFrame) cancelAnimationFrame(this.repositionFrame);

      this.isRepositioning = true;
      this.track.classList.add('is-repositioning');
      this.track.scrollLeft = left;

      this.repositionFrame = requestAnimationFrame(() => {
        this.track.classList.remove('is-repositioning');
        this.isRepositioning = false;
        this.repositionFrame = null;
      });
    }

    normalizeLoopPosition() {
      if (!this.loopEnabled) {
        this.updatePagination();
        return false;
      }

      const virtualIndex = this.getVirtualIndex();
      let normalizedIndex = virtualIndex;

      if (virtualIndex < this.cloneCount) {
        normalizedIndex = virtualIndex + this.originalCards.length;
      } else if (virtualIndex >= this.cloneCount + this.originalCards.length) {
        normalizedIndex = virtualIndex - this.originalCards.length;
      }

      if (normalizedIndex !== virtualIndex) {
        this.scrollToVirtualIndex(normalizedIndex, 'auto');
      }

      this.updatePagination();
      return normalizedIndex !== virtualIndex;
    }

    goTo(index, requestedBehavior = 'smooth', preferOriginal = false) {
      const pageCount = this.getPageCount();
      if (pageCount <= 1) return;

      const logicalIndex = ((index % pageCount) + pageCount) % pageCount;
      this.currentLogicalIndex = logicalIndex;
      this.navigationQueue = [];

      if (!this.loopEnabled) {
        this.scrollToVirtualIndex(logicalIndex, requestedBehavior);
        return;
      }

      const originalTarget = this.cloneCount + logicalIndex;
      let targetIndex = originalTarget;

      if (!preferOriginal) {
        const currentVirtualIndex = this.getVirtualIndex();
        const candidates = [
          logicalIndex,
          originalTarget,
          this.cloneCount + this.originalCards.length + logicalIndex
        ];

        targetIndex = candidates.reduce((closest, candidate) => {
          return Math.abs(candidate - currentVirtualIndex) < Math.abs(closest - currentVirtualIndex)
            ? candidate
            : closest;
        }, originalTarget);
      }

      if (targetIndex === this.getVirtualIndex()) {
        this.isNavigationAnimating = false;
        this.updatePagination();
        return;
      }

      const behavior = this.scrollToVirtualIndex(targetIndex, requestedBehavior);
      this.isNavigationAnimating = behavior === 'smooth';

      if (behavior === 'auto') {
        requestAnimationFrame(() => this.handleScrollSettled());
      }
    }

    move(direction) {
      if (!this.loopEnabled) return;

      const normalizedDirection = direction < 0 ? -1 : 1;

      if (this.isPointerDragging || this.isNavigationAnimating || this.isRepositioning) {
        this.navigationQueue.push(normalizedDirection);
        return;
      }

      this.performMove(normalizedDirection);
    }

    performMove(direction) {
      if (!this.loopEnabled) return;

      if (this.normalizeLoopPosition()) {
        this.isNavigationAnimating = true;
        requestAnimationFrame(() => {
          this.isNavigationAnimating = false;
          this.performMove(direction);
        });
        return;
      }

      const virtualIndex = this.getVirtualIndex();
      this.currentLogicalIndex = ((this.getCurrentPage() + direction) % this.getPageCount() + this.getPageCount()) % this.getPageCount();
      const behavior = this.scrollToVirtualIndex(virtualIndex + direction);
      this.isNavigationAnimating = behavior === 'smooth';

      if (behavior === 'auto') {
        requestAnimationFrame(() => this.handleScrollSettled());
      }
    }

    startAutoplay() {
      if (this.dataset.autoplay !== 'true') return;
      if (this.reducedMotion.matches || document.hidden || this.dataset.hasOverflow !== 'true') return;
      if (this.autoplayTimer) return;

      const delay = Math.max(1500, Number(this.dataset.autoplayDelay) || 5000);
      this.autoplayTimer = window.setInterval(() => this.move(1), delay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    startPointerDrag(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.dataset.hasOverflow !== 'true') return;

      this.isPointerDragging = true;
      this.hasPointerMoved = false;
      this.navigationQueue = [];
      this.isNavigationAnimating = false;
      this.pointerStartX = event.clientX;
      this.pointerStartScroll = this.track.scrollLeft;
      this.track.setPointerCapture(event.pointerId);
      this.track.classList.add('is-dragging');
      this.stopAutoplay();
    }

    handlePointerDrag(event) {
      if (!this.isPointerDragging) return;

      const distance = event.clientX - this.pointerStartX;
      if (Math.abs(distance) > 4) this.hasPointerMoved = true;
      this.track.scrollLeft = this.pointerStartScroll - distance;
    }

    endPointerDrag(event) {
      if (!this.isPointerDragging) return;

      this.isPointerDragging = false;
      this.track.classList.remove('is-dragging');

      if (this.track.hasPointerCapture(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }

      if (this.hasPointerMoved) {
        this.scrollToVirtualIndex(this.getVirtualIndex());
      }

      this.startAutoplay();
    }
  }

  customElements.define('customer-reviews-carousel', CustomerReviewsCarousel);
})();
